import type { MouseEvent } from 'react'

import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import type { BookingFormValues } from 'pages/kyrgyzstan/booking/model'

import { PrivacyPolicyModel } from 'shared/api/PrivacyPolicyModel'
import { CurrencySom } from 'shared/constants'
import { usePrivacyPolicyDocument } from 'shared/hooks'
import { priceShow } from 'shared/lib/priceShow'
import { Button } from 'shared/ui/button'
import { CheckBoxSimple } from 'shared/ui/check-box-simple'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'

interface BookingSummaryCardProps {
  amount: number
  isLoading?: boolean
  isSubmitDisabled?: boolean
}

export const BookingSummaryCard = observer(
  ({
    amount,
    isLoading = false,
    isSubmitDisabled = false,
  }: BookingSummaryCardProps) => {
    const { t } = useTranslation()
    const { control } = useFormContext<BookingFormValues>()
    const { openPrivacyPolicy } = usePrivacyPolicyDocument()

    const handleLinkClick = (
      event: MouseEvent<HTMLSpanElement>,
      field: keyof PrivacyPolicyModel,
    ) => {
      event.stopPropagation()
      void openPrivacyPolicy(field)
    }

    return (
      <Paper
        padding={[12, 16, 28]}
        className={s.summaryCard}
      >
        <div className={s.summaryHeader}>
          <span className="bold-16">
            {t('kyrgyzstan_block.booking.summary.title')}
          </span>
          <span className="extra-bold-18">
            {priceShow(String(amount))} {CurrencySom}
          </span>
        </div>

        <Controller
          name="isAgreementAccepted"
          control={control}
          render={({ field }) => {
            const isButtonDisabled =
              !field.value || isLoading || isSubmitDisabled

            return (
              <>
                <div className={s.summaryAgreement}>
                  <CheckBoxSimple
                    isChecked={field.value}
                    isDisabled={isLoading}
                    onClick={(value) => field.onChange(Boolean(value))}
                  />
                  <p className={clsx('regular-12', s.summaryText)}>
                    {t('kyrgyzstan_block.booking.summary.agreement')}{' '}
                    <span
                      className={s.link}
                      onClick={(event) =>
                        handleLinkClick(event, 'kyrgyzstan_public_offer')
                      }
                    >
                      {t('kyrgyzstan_block.booking.summary.offer')}
                    </span>{' '}
                    {t('kyrgyzstan_block.booking.summary.and')}{' '}
                    <span
                      className={s.link}
                      onClick={(event) =>
                        handleLinkClick(event, 'base_public_offer')
                      }
                    >
                      {t('kyrgyzstan_block.booking.summary.privacy')}
                    </span>
                  </p>
                </div>

                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={isButtonDisabled}
                >
                  <span className="semi-bold-17">{t('continue')}</span>
                </Button>
              </>
            )
          }}
        />
      </Paper>
    )
  },
)
